import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import RTButton from './RTButton';
import './Toast.css'; // <-- Import the CSS file

// ToastConfirm: A reusable confirmation toast
export const ToastConfirm = ({ show, message = "Are you sure?", onConfirm, onCancel, confirmText = "Confirm", cancelText = "Cancel", confirmColor = "#153C7F" }) => {
  if (!show) return null;
  return (
    <div className="toast-confirm-overlay">
      <div className="toast-confirm">
        <div style={{ marginBottom: 12, fontWeight: 500 }}>{message}</div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <RTButton
            style={{ backgroundColor: confirmColor }}
            onClick={onConfirm}
          >
            {confirmText}
          </RTButton>
          <RTButton
            style={{ backgroundColor: '#e0e0e0', color: '#153C7F' }}
            onClick={onCancel}
          >
            {cancelText}
          </RTButton>
        </div>
      </div>
    </div>
  );
};

// ToastSuccess: A reusable success toast
export const ToastSuccess = ({ show, message = "Success!", onClose, duration = 2500 }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [show, onClose, duration]);
  if (!show) return null;
  return (
    <div className="toast-success-overlay">
      <div className="toast-success">
        <div style={{ fontWeight: 500, color: "#153C7F" }}>{message}</div>
      </div>
    </div>
  );
};

// ToastError: A reusable error toast
export const ToastError = ({ show, message = "Something went wrong!", onClose, autoClose = true, duration = 3000 }) => {
  useEffect(() => {
    if (show && autoClose) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [show, onClose, duration, autoClose]);
  if (!show) return null;
  return (
    <div className="toast-error-overlay">
      <div className="toast-error">
        <div style={{ fontWeight: 500, color: "#b71c1c", flex: 1 }}>{message}</div>
        <RTButton
          style={{ 
            backgroundColor: 'transparent', 
            color: '#b71c1c', 
            border: '1px solid #b71c1c',
            padding: '4px 8px',
            fontSize: '12px',
            minWidth: 'auto',
            marginLeft: '12px'
          }}
          onClick={onClose}
        >
          ✕
        </RTButton>
      </div>
    </div>
  );
};

// ToastWarning: A reusable warning toast
export const ToastWarning = ({ show, message = "Warning!", onClose, autoClose = true, duration = 3500 }) => {
  useEffect(() => {
    if (show && autoClose) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [show, onClose, duration, autoClose]);
  if (!show) return null;
  return (
    <div className="toast-warning-overlay">
      <div className="toast-warning">
        <div style={{ fontWeight: 500, color: "#f57c00", flex: 1 }}>{message}</div>
        <RTButton
          style={{ 
            backgroundColor: 'transparent', 
            color: '#f57c00', 
            border: '1px solid #f57c00',
            padding: '4px 8px',
            fontSize: '12px',
            minWidth: 'auto',
            marginLeft: '12px'
          }}
          onClick={onClose}
        >
          ✕
        </RTButton>
      </div>
    </div>
  );
};

// Hooks to trigger toasts from anywher
let toastRoot = null;
function ensureToastRoot() {
  if (!toastRoot) {
    let div = document.getElementById('toast-root');
    if (!div) {
      div = document.createElement('div');
      div.id = 'toast-root';
      document.body.appendChild(div);
    }
    toastRoot = createRoot(div);
  }
}

export function toastSuccess(msg) {
  ensureToastRoot();
  toastRoot.render(
    <ToastSuccess show={true} message={msg} onClose={() => toastRoot.render(null)} />
  );
}

export function toastError(msg, autoClose = true) {
  ensureToastRoot();
  toastRoot.render(
    <ToastError show={true} message={msg} autoClose={autoClose} onClose={() => toastRoot.render(null)} />
  );
}

export function toastWarning(msg, autoClose = true) {
  ensureToastRoot();
  toastRoot.render(
    <ToastWarning show={true} message={msg} autoClose={autoClose} onClose={() => toastRoot.render(null)} />
  );
}

// For backwards compatibility - auto-closing error toast
export function toastErrorAutoClose(msg, duration = 3000) {
  toastError(msg, true);
}

export function toastConfirm(show, message, onConfirm, onCancel) {
  ensureToastRoot();
  return new Promise((resolve) => {
    const handleConfirm = () => {
      resolve(true);
      toastRoot.render(null);
      onConfirm && onConfirm();
    };
    const handleCancel = () => {
      resolve(false);
      toastRoot.render(null);
      onCancel && onCancel();
    };
    toastRoot.render(
      <ToastConfirm
        show={show}
        message={message}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    );
  });
}